/**
 * PerformanceStats - Per-subsystem frame timing and renderer metrics
 *
 * Collects granular timing for each subsystem in the game loop,
 * plus Three.js renderer.info stats. Updated every frame internally,
 * flushed to the store at 5 Hz to avoid React churn.
 *
 * Usage:
 *   perfStats.begin('physics');
 *   // ... do physics ...
 *   perfStats.end('physics');
 *
 *   // After render:
 *   perfStats.captureRendererInfo(renderer);
 *   perfStats.endFrame();
 *
 * THE FRAME, IN THREE TIERS (what the Performance panel draws):
 *   - `frameInterval`: the rAF delta — the only number that IS the frame. Measured, not derived: it is
 *     ≈ max(main-thread time, GPU time, refresh period), rounded up to whole vsyncs, and there is no
 *     sum to display. `gpu` is the GPU's candidate for having set it.
 *   - Main thread: `gameUpdate` (inside the rAF callback) + `offFrame` (worker replies, socket payloads,
 *     IndexedDB callbacks — the work that runs BETWEEN callbacks and that no callback-scoped timer
 *     ever saw). What is left of the interval is idle / vsync wait.
 *   - Inside the update: `render` (CPU submit), `physics`, and `voxelUpdate` — the World — which is
 *     itself split into the stages below, each timed where it runs (VoxelWorld.update).
 */

import * as THREE from 'three';
import { useGameStore } from '../../state/store';
import { refreshPeriodMs } from '../voxel/frameBudget';

/** Subsystem timing keys */
export type PerfSection =
  | 'gameUpdate'     // Total update-callback time (everything below except offFrame)
  | 'offFrame'       // Main-thread work OUTSIDE the callback: worker onmessage, socket payloads, IDB
  | 'offGen'         // ...of which: terrain-worker replies (columns, tiles, coarse bundles)
  | 'offMesh'        // ...of which: mesh-worker replies
  | 'offNet'         // ...of which: socket payload decode
  | 'offIdb'         // ...of which: IndexedDB persist-queue flush
  | 'physics'        // Player physics + movement
  | 'render'         // the scene pass + post pipeline + arm — CPU submit
  | 'players'        // Remote player interpolation
  | 'environment'    // Day/night + sky + lighting apply
  | 'buildPreview'   // Build preview meshing
  | 'debugViz'       // chunk-bounds / coord-label overlay rebuild
  | 'voxelUpdate'    // VoxelWorld.update in full — the World; spend against `voxelBudget`
  // ---- World stages, in frame order (all inside voxelUpdate) ----
  | 'applies'        // finished worker meshes → ChunkGeometry + copy into the TerrainBatch
  | 'ingest'         // lod-0 arrivals: visibility bits, remesh enqueue, margin re-trigger, relight fan-out — EXCLUDING light
  | 'lightSun'       // lod-0 sunlight column + BFS for the chunk being ingested
  | 'lightNbr'       // neighbour relights an arrival caused (immediate + deferred drain)
  | 'lightCoarse'    // coarse standalone relight (player-edited chunks; worker lights the rest)
  | 'lightEdit'      // build preview / commit / undo region relights
  | 'bfs'            // visibility BFS + margin-source expansion + base request plan (on chunk cross / frontier change)
  | 'stream'         // LOD gate, shell recompute, coarse + base request issue
  | 'walk'           // the LOD selection walk over the onion → display decisions
  | 'evict'          // incomplete-chunk heal + lod-0 / coarse evictions
  | 'asserts'        // DEV invariant checks (dev server only)
  | 'commit'         // display flips — EXCLUDING skirts
  | 'skirts'         // SeamStitcher.flush inside the commit
  | 'compact'        // TerrainBatch.tick — room-making: pre-emptive growth, or compaction when it avoids one
  | 'remesh'         // dispatch: remesh queue sort + grid expansion + worker post
  ;

/** Every section, in the order the accumulators are created. */
export const PERF_SECTIONS: readonly PerfSection[] = [
  'gameUpdate', 'offFrame', 'physics', 'render', 'players', 'environment', 'buildPreview', 'debugViz',
  'voxelUpdate', 'applies', 'ingest', 'lightSun', 'lightNbr', 'lightCoarse', 'lightEdit',
  'bfs', 'stream', 'walk', 'evict', 'asserts',
  'commit', 'skirts', 'compact', 'remesh',
  'offGen', 'offMesh', 'offNet', 'offIdb',
];

/** The World's stages — the pool the panel ranks (with render / physics / offFrame / debugViz). */
export const WORLD_STAGES: readonly PerfSection[] = [
  'applies', 'ingest', 'lightSun', 'lightNbr', 'lightCoarse', 'lightEdit', 'bfs', 'stream', 'walk', 'evict', 'asserts', 'commit', 'skirts', 'compact', 'remesh',
];

/**
 * NESTED-EXCLUSIVE SECTIONS. The `light*` stages run inside `ingest`/`stream`, `skirts` inside `commit`; without this
 * the parent's row double-counted the child and "ingest" read as "light + a bit". When a child listed
 * here ends while its parent is open, its time is taken OUT of the parent. Totals (`gameUpdate`,
 * `voxelUpdate`) deliberately stay inclusive — they are the tiers the stages sum to.
 */
const EXCLUDE_FROM: Partial<Record<PerfSection, readonly PerfSection[]>> = {
  lightSun: ['ingest'],
  lightNbr: ['ingest'],
  lightCoarse: ['ingest', 'stream'],
  skirts: ['commit'],
};
// The `off*` children are deliberately NOT excluded from `offFrame`: offFrame stays the off-frame
// TOTAL (the CPU bar's orange segment) and the children attribute it in the ranked table.

/** Snapshot of per-frame performance data */
export interface PerfSnapshot {
  // Per-subsystem times in ms (rolling averages)
  gameUpdate: number;
  offFrame: number;
  physics: number;
  render: number;
  players: number;
  environment: number;
  buildPreview: number;
  /**
   * The chunk-bounds / coord-label overlay. Its own term because it is NOT inside voxelUpdate and is
   * excluded from the audit's PERF breach check, so it could eat a whole frame and no number moved:
   * measured 16.3 ms/frame on the pass that builds the boxes and a permanent ~2.4 ms floor after.
   */
  debugViz: number;
  voxelUpdate: number;
  applies: number;
  ingest: number;
  lightSun: number;
  lightNbr: number;
  lightCoarse: number;
  lightEdit: number;
  offGen: number;
  offMesh: number;
  offNet: number;
  offIdb: number;
  bfs: number;
  stream: number;
  walk: number;
  evict: number;
  asserts: number;
  commit: number;
  skirts: number;
  compact: number;
  remesh: number;

  /**
   * SESSION-MAX per-frame value of each section, ms — the worst frame that stage has ever had since
   * the page loaded (or the last reset). Jank lives here, not in the average, and a session max settles
   * within seconds of the worst case instead of shuffling with a rolling window. Reset from the panel.
   */
  peak: Record<PerfSection, number>;

  // Three.js renderer info. Triangles / textures / programs are absent: they track the scene's size,
  // not its cost, and never localized a problem. `geometries` IS diagnostic: it must track the
  // batch count, and a residue growing over a session means leaked GPU buffers (a leak this
  // counter caught once already). `drawCalls` is back because it is the number under test: CPU
  // render submit is ~28 µs a call through ANGLE on the laptop, and the batching work exists to
  // shrink it — a shadow frame shows the casters counted twice, which is real.
  geometries: number;
  drawCalls: number;

  /** GPU time of the render, ms — EXT_disjoint_timer_query (see GpuTimer). 0 where unavailable. The
   *  CPU `render` figure cannot see a GPU-bound frame; this can. */
  gpu: number;

  /** rAF interval, ms (rolling average) — the frame as the display sees it, GPU-bound frames included. */
  frameInterval: number;
  /** The display's refresh period inferred from the shortest recent interval (frameBudget.refreshPeriodMs). */
  refreshMs: number;
  /** This frame's main-thread voxel allowance, ms — see voxel/frameBudget.ts. `voxelUpdate` is the spend. */
  voxelBudget: number;

  // Voxel-specific
  remeshQueueSize: number;
  remeshParked: number;   // queue entries deferred by design (margin/occluder chunks) — not pending work
  pendingChunks: number;
  meshDispatches: number;   // cumulative mesh-worker dispatches (streaming re-mesh churn gauge)
  /** The shape of the commit's skirt cost: is a spike many owners or one expensive one? */
  skirt: SkirtGauges;

  // Memory (when available)
  jsHeapMB: number;
}

export interface SkirtGauges {
  /** Owners built unbudgeted this flush because a flip named them; all distinct owners the commit named. */
  priority: number;
  owners: number;
  /** The most owners one admitted transaction named this commit, and the session's worst. */
  maxTxOwners: number;
  peakTxOwners: number;
  /** Owner build time this flush in total, its single most expensive owner, and the session's worst. */
  ownerMs: number;
  ownerMaxMs: number;
  ownerMaxKey: string;
  peakOwnerMs: number;
  peakOwnerKey: string;
  peakFlushMs: number;
  /** Face×layer emitter runs this flush, and the ones the empty-face cache answered instead. */
  facesEmitted: number;
  facesSkipped: number;
}

export const zeroSkirt = (): SkirtGauges => ({
  priority: 0, owners: 0, maxTxOwners: 0, peakTxOwners: 0,
  ownerMs: 0, ownerMaxMs: 0, ownerMaxKey: '', peakOwnerMs: 0, peakOwnerKey: '', peakFlushMs: 0,
  facesEmitted: 0, facesSkipped: 0,
});

function zeroSections(): Record<PerfSection, number> {
  const o = {} as Record<PerfSection, number>;
  for (const k of PERF_SECTIONS) o[k] = 0;
  return o;
}

export const EMPTY_SNAPSHOT: PerfSnapshot = {
  ...zeroSections(),
  peak: zeroSections(),
  geometries: 0, drawCalls: 0, gpu: 0, frameInterval: 0, refreshMs: 1000 / 60, voxelBudget: 0,
  remeshQueueSize: 0, remeshParked: 0, pendingChunks: 0,
  meshDispatches: 0, skirt: zeroSkirt(), jsHeapMB: 0,
};

/** Rolling average window size (frames) */
const AVG_WINDOW = 60;
/** rAF-interval window (frames): ~2 s at 60 Hz, long enough to contain a full-rate frame. */
const FRAME_INTERVAL_WINDOW = 120;

/** Store flush interval (ms) */
const FLUSH_INTERVAL_MS = 200; // 5Hz — enough for UI, minimal React overhead

class PerformanceStatsCollector {
  // Rolling window of PER-FRAME totals (one sample per frame, AVG_WINDOW frames).
  private accum: Record<PerfSection, number[]> = (() => {
    const o = {} as Record<PerfSection, number[]>;
    for (const k of PERF_SECTIONS) o[k] = [];
    return o;
  })();

  // Per-frame accumulator: sums every begin/end pair for a section within the
  // current frame, so a section timed many times per frame (e.g. lightSun — one
  // call per chunk relight) reports its per-frame TOTAL, not a per-call average.
  private frameSum = new Map<PerfSection, number>();

  // In-flight timers
  private starts = new Map<PerfSection, number>();
  /** Time to take out of an open parent when it ends — see EXCLUDE_FROM. */
  private nestedInto = new Map<PerfSection, number>();

  // Latest computed snapshot
  private snapshot: PerfSnapshot = { ...EMPTY_SNAPSHOT, peak: zeroSections() };

  // Flush throttle
  private lastFlush = 0;

  // Additional per-frame counters (set externally)
  private _remeshQueueSize = 0;
  private _remeshParked = 0;
  private _pendingChunks = 0;
  private _meshDispatches = 0;

  /** Start timing a section */
  begin(section: PerfSection): void {
    this.starts.set(section, performance.now());
  }

  /** End timing a section — adds to this frame's running total for the section. */
  end(section: PerfSection): void {
    const start = this.starts.get(section);
    if (start === undefined) return;
    let elapsed = performance.now() - start;
    this.starts.delete(section);
    // A child's time leaves its open parents (EXCLUDE_FROM); a parent settles its own debt on exit.
    const parents = EXCLUDE_FROM[section];
    if (parents) {
      for (const p of parents) {
        if (this.starts.has(p)) this.nestedInto.set(p, (this.nestedInto.get(p) ?? 0) + elapsed);
      }
    }
    const owed = this.nestedInto.get(section);
    if (owed !== undefined) { elapsed -= owed; this.nestedInto.delete(section); }
    this.frameSum.set(section, (this.frameSum.get(section) ?? 0) + elapsed);
  }

  /** Capture renderer.info after render call */
  captureRendererInfo(renderer: THREE.WebGLRenderer): void {
    this.snapshot.geometries = renderer.info.memory.geometries;
    this.snapshot.drawCalls = renderer.info.render.calls; // this frame's, including the shadow pass
  }

  /** Set voxel queue stats (called from VoxelWorld) */
  setVoxelQueueStats(remeshQueueSize: number, pendingChunks: number, remeshParked = 0): void {
    this._remeshQueueSize = remeshQueueSize;
    this._remeshParked = remeshParked;
    this._pendingChunks = pendingChunks;
  }

  /** Latest GPU render time, ms (GpuTimer rolling average). */
  setGpuMs(ms: number): void {
    this.snapshot.gpu = ms;
  }

  // rAF intervals: a rolling average (the frame budget's proportional floor, and the overlay) and the
  // window's minimum (the display's refresh period — see frameBudget.refreshPeriodMs). Fed from the
  // loop's rAF timestamps, not from performance.now() at endFrame, so the minimum is a real vsync.
  private frameIntervals: number[] = [];
  private frameIntervalSum = 0;
  private frameIntervalCount = 0;
  private _minFrameInterval = Number.POSITIVE_INFINITY;

  /** Call once per frame with the rAF delta. */
  setFrameInterval(deltaMs: number): void {
    this.frameIntervals.push(deltaMs);
    this.frameIntervalSum += deltaMs;
    if (this.frameIntervals.length > FRAME_INTERVAL_WINDOW) this.frameIntervalSum -= this.frameIntervals.shift()!;
    this.snapshot.frameInterval = this.frameIntervalSum / this.frameIntervals.length;
    // A new low takes effect at once; otherwise the window's minimum is re-derived every 64 frames so
    // a display-rate change (window moved to another monitor) is forgotten within the window.
    if (deltaMs < this._minFrameInterval) this._minFrameInterval = deltaMs;
    if ((++this.frameIntervalCount & 63) === 0) this._minFrameInterval = Math.min(...this.frameIntervals);
    this.snapshot.refreshMs = refreshPeriodMs(this._minFrameInterval);
  }

  /** Shortest rAF interval in the window, ms (Infinity before the first frame). */
  get minFrameIntervalMs(): number { return this._minFrameInterval; }

  /** This frame's voxel allowance, ms — set by VoxelWorld.update. */
  setVoxelBudget(ms: number): void {
    this.snapshot.voxelBudget = ms;
  }

  /** Forget the session peaks (the panel's ↺) — e.g. after the cold load's one-off spikes. */
  resetSessionPeaks(): void {
    this.snapshot.peak = zeroSections();
    const s = this.snapshot.skirt;
    s.peakTxOwners = 0; s.peakOwnerMs = 0; s.peakOwnerKey = ''; s.peakFlushMs = 0;
  }

  /** The commit's skirt cost shape this frame (called from VoxelWorld after the commit); peaks are kept. */
  setSkirtGauges(g: Pick<SkirtGauges, 'priority' | 'owners' | 'maxTxOwners' | 'ownerMs' | 'ownerMaxMs' | 'ownerMaxKey' | 'facesEmitted' | 'facesSkipped'>): void {
    const s = this.snapshot.skirt;
    s.priority = g.priority; s.owners = g.owners; s.maxTxOwners = g.maxTxOwners;
    s.ownerMs = g.ownerMs; s.ownerMaxMs = g.ownerMaxMs; s.ownerMaxKey = g.ownerMaxKey;
    s.facesEmitted = g.facesEmitted; s.facesSkipped = g.facesSkipped;
    if (g.maxTxOwners > s.peakTxOwners) s.peakTxOwners = g.maxTxOwners;
    if (g.ownerMaxMs > s.peakOwnerMs) { s.peakOwnerMs = g.ownerMaxMs; s.peakOwnerKey = g.ownerMaxKey; }
    if (g.ownerMs > s.peakFlushMs) s.peakFlushMs = g.ownerMs;
  }

  /** Cumulative mesh-worker dispatch count (called from VoxelWorld) — churn gauge for streaming. */
  setMeshDispatches(total: number): void {
    this._meshDispatches = total;
  }

  /** Call at the end of each frame to compute averages and flush */
  endFrame(): void {
    // Fold a sample for EVERY section into its rolling window each frame — using the
    // section's per-frame total, or 0 if it did no work this frame. Pushing zeros for
    // idle frames is what lets an intermittent section (e.g. lightSun) decay back to 0
    // when nothing is happening, instead of freezing at its last active average.
    const peak = this.snapshot.peak;
    for (const key of PERF_SECTIONS) {
      const v = this.frameSum.get(key) ?? 0;
      const buf = this.accum[key];
      buf.push(v);
      if (buf.length > AVG_WINDOW) buf.shift();
      if (v > peak[key]) peak[key] = v; // session max — see PerfSnapshot.peak
    }
    this.frameSum.clear();
    this.nestedInto.clear();

    // Compute rolling averages
    for (const key of PERF_SECTIONS) {
      const buf = this.accum[key];
      if (buf.length === 0) {
        this.snapshot[key] = 0;
      } else {
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i];
        this.snapshot[key] = sum / buf.length;
      }
    }

    this.snapshot.remeshQueueSize = this._remeshQueueSize;
    this.snapshot.remeshParked = this._remeshParked;
    this.snapshot.pendingChunks = this._pendingChunks;
    this.snapshot.meshDispatches = this._meshDispatches;

    // JS heap (Chrome-only)
    const mem = (performance as any).memory;
    if (mem) {
      this.snapshot.jsHeapMB = Math.round(mem.usedJSHeapSize / (1024 * 1024));
    }

    // Flush to store at throttled rate (the peak record is copied so React sees a new reference).
    const now = performance.now();
    if (now - this.lastFlush >= FLUSH_INTERVAL_MS) {
      this.lastFlush = now;
      useGameStore.getState().setPerfStats({ ...this.snapshot, peak: { ...this.snapshot.peak } });
    }
  }

  /** Get latest snapshot (for non-React reads) */
  getSnapshot(): Readonly<PerfSnapshot> {
    return this.snapshot;
  }
}

/** Singleton instance */
export const perfStats = new PerformanceStatsCollector();
